import { Icon } from '../day/Icon.tsx';
import { clampVisualPage } from './visual-pages.ts';

interface Props {
  current: number;
  total: number;
  onJump: (page: number) => void;
}

/**
 * Position and direct navigation for a note's presentation-only sheets.
 *
 * The select is the practical long-note path: it reaches sheet 27 directly,
 * while the arrows are one-step conveniences. Neither is pagination. Ordinary
 * scrolling and the one continuous textarea/article remain available at all
 * times, and no content is mounted or unmounted when this value changes.
 */
export function VisualPageNavigator({ current, total, onJump }: Props) {
  const page = clampVisualPage(current, total);
  const count = Math.max(1, Math.floor(total));

  return (
    <nav className="vpages" aria-label="Visual pages" aria-describedby="visual-pages-help">
      <span id="visual-pages-help" className="sr">
        Visual pages are reading landmarks only. This note is one continuous document.
      </span>
      <button
        type="button"
        aria-label="Previous visual page"
        disabled={page === 1}
        onClick={() => onJump(page - 1)}
      >
        <Icon name="chevL" />
      </button>
      <select
        aria-label="Go to visual page"
        value={page}
        onChange={(event) => onJump(Number(event.target.value))}
      >
        {Array.from({ length: count }, (_, index) => {
          const option = index + 1;
          return (
            <option key={option} value={option}>
              Page {option} / {count}
            </option>
          );
        })}
      </select>
      <button
        type="button"
        aria-label="Next visual page"
        disabled={page === count}
        onClick={() => onJump(page + 1)}
      >
        <Icon name="chevR" />
      </button>
    </nav>
  );
}
